import { GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import styled from 'styled-components';

import Office from '../backend/s3/office';
import Flat from '../backend/s3/flat';
import { SITE_URL } from '../common/consts';
import { IContact } from '../common/model/mailchimp/contact.model';
import { IOffice } from '../common/model/office.model';
import useI18n from '../common/hooks/useI18n';
import useMailchimpService from '../common/hooks/mailchimpService';
import {
  BuyHouseOption,
  computeBuyHouseOptions,
} from '../common/helpers/buyHouseOption.utils';

import { ContactFormSection } from '../components/contact';
import { Header, OfficesSection, Footer, Head } from '../components/shared';
import { useMemo } from 'react';
import { generateContactPageStructuredData } from '../common/helpers/structuredData.utils';

interface StaticProps {
  offices: IOffice[];
  buyHouseOptions: BuyHouseOption[];
}

type Props = StaticProps;

const Layout = styled.div`
  display: flex;
  flex: auto;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 0;

  font-style: ${(props) => props.theme.font.style};
`;

const Content = styled.main`
  position: relative;
  flex: auto;
  margin-top: ${(props) => props.theme.headerHeight};
  background-color: ${(props) => props.theme.colors.grey};

  @media ${(props) => props.theme.breakpoints.mdd} {
    margin-top: 0;
  }
`;

const ContactPage = ({ offices, buyHouseOptions }: Props): JSX.Element => {
  const router = useRouter();
  const i18n = useI18n();
  const mailchimpService = useMailchimpService();

  const structuredData = useMemo(() => generateContactPageStructuredData(), []);

  const onSendButtonClick = (contact: IContact) => {
    mailchimpService.subscribe(contact, router, i18n);
  };

  return (
    <Layout>
      <Head
        title={i18n.t('contact.metaTitle')}
        description={i18n.t('contact.metaDescription')}
        canonicalUrl={`${SITE_URL}/contacto`}
        metaRobotsNode={<meta name="robots" content="noindex,nofollow" />}
      >
        {structuredData ? (
          <script
            id="structured-data-script"
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(structuredData),
            }}
          />
        ) : null}
      </Head>

      <Header offices={offices} buyHouseOptions={buyHouseOptions} />

      <Content>
        <ContactFormSection onSendButtonClick={onSendButtonClick} />
        <OfficesSection offices={offices} />
      </Content>

      <Footer />
    </Layout>
  );
};

export const getStaticProps: GetStaticProps<StaticProps> = async () => {
  const offices = await Office.getOffices();
  const [_, searchOptions] = await Flat.getFlats();

  const buyHouseOptions = computeBuyHouseOptions(searchOptions);

  return {
    props: {
      offices,
      buyHouseOptions,
    },
  };
};

export default ContactPage;
